import { useMemo } from 'react';
import type { ProjectType } from '@atlassian/jira-common-constants/src/index.tsx';
import {
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
	CUSTOMER_SERVICE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { PROJECT_SETTINGS_PAGE_MODULE } from '@atlassian/jira-forge-ui-constants/src/constants.tsx';
import type {
	ExtensionEnvironment,
	ProjectSettingsPage,
	Section,
} from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import { parseExtensionId } from '@atlassian/jira-forge-ui-utils-internal/src/utils/parse-extension-id/index.tsx';
import {
	isExtensionHiddenByAppAccessRules,
	isExtensionVisible,
} from '@atlassian/jira-forge-ui-utils/src/utils/extension/index.tsx';
import { forgeProjectSettingsModuleResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/forge/index.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { useForgeItemStack } from '../../common/utils/find-item-stack/index.tsx';
import { toSections } from '../../common/utils/subpages/index.tsx';
import type {
	UseNavigationResourceReturnWithStack,
	UseNavigationResourceReturnWithAN,
} from '../types.tsx';

export const useProjectSettingsForgeApps = (): UseNavigationResourceReturnWithAN<
	ProjectSettingsPage[]
> => {
	const { data: projectContext } = useProjectContext();
	const { data, loading, error } = useResource(forgeProjectSettingsModuleResource);
	const projectSettingsData = useMemo(() => data?.filter?.(isExtensionVisible) || null, [data]);
	const accessNarrowedData = useMemo(
		() => data?.filter?.(isExtensionHiddenByAppAccessRules) || null,
		[data],
	);
	const isAdmin = useIsAdmin();
	const isSiteAdmin = useIsSiteAdmin();
	const hasPermissions = useMemo(
		() => (projectContext?.isProjectAdmin || isAdmin || isSiteAdmin) === true,
		[isAdmin, isSiteAdmin, projectContext?.isProjectAdmin],
	);

	return useMemo<UseNavigationResourceReturnWithAN<ProjectSettingsPage[]>>(
		() => ({
			data: projectSettingsData ? (hasPermissions && projectSettingsData) || [] : null,
			accessNarrowedData,
			loading,
			error,
		}),
		[projectSettingsData, accessNarrowedData, loading, error, hasPermissions],
	);
};

const appsProjectTypeRouteMap: Record<'tmp' | 'cmp', Record<ProjectType, string | undefined>> = {
	tmp: {
		[CORE_PROJECT]: 'core',
		[SOFTWARE_PROJECT]: 'software',
		[SERVICE_DESK_PROJECT]: 'servicedesk',
		// it's genuinely missing Jira Product Discovery and CUSTOMER_SERVICE_PROJECT support
		[PRODUCT_DISCOVERY_PROJECT]: undefined,
		[CUSTOMER_SERVICE_PROJECT]: undefined,
	},
	cmp: {
		[CORE_PROJECT]: 'core',
		[SOFTWARE_PROJECT]: 'software/c',
		[SERVICE_DESK_PROJECT]: 'servicedesk',
		[PRODUCT_DISCOVERY_PROJECT]: undefined,
		[CUSTOMER_SERVICE_PROJECT]: undefined,
	},
};

export const getItemHref = (
	extension: ProjectSettingsPage,
	projectType: ProjectType,
	projectKey: string,
	isSimplified: boolean,
) => {
	const { appId, envId } = parseExtensionId(extension.id);
	return `/jira/${
		appsProjectTypeRouteMap[isSimplified ? 'tmp' : 'cmp'][projectType]
	}/projects/${projectKey}/settings/apps/${appId}/${envId}`;
};

export type ForgeProjectSettingsItem = {
	id: string;
	url: string;
	name: string;
	iconUrl?: string;
	sections?: Section[];
	environmentType: ExtensionEnvironment;
	environmentKey: string;
	moduleName: string;
};

export const useProjectSettingsForgeAppsForMenu = (
	routeName?: string,
	parentStack?: string[],
): UseNavigationResourceReturnWithStack<ForgeProjectSettingsItem[]> => {
	const { data: projectContext } = useProjectContext();
	const { data, loading, error } = useProjectSettingsForgeApps();
	const stack = useForgeItemStack(data, routeName, parentStack);
	const { projectType, projectKey, simplified: isSimplified } = projectContext || {};

	return useMemo<UseNavigationResourceReturnWithStack<ForgeProjectSettingsItem[]>>(
		() => ({
			data: data
				? data.map((extension) => ({
						id: extension.id,
						// @ts-expect-error - TS2345 - Argument of type 'ProjectType | undefined' is not assignable to parameter of type 'ProjectType'.
						url: getItemHref(extension, projectType, projectKey, isSimplified),
						name: extension.properties.title,
						iconUrl: extension.properties.icon,
						environmentType: extension.environmentType,
						environmentKey: extension.environmentKey,
						moduleName: PROJECT_SETTINGS_PAGE_MODULE,
						sections: toSections(extension),
					}))
				: null,
			loading,
			error,
			stack,
		}),
		[data, loading, error, stack, projectType, projectKey, isSimplified],
	);
};
